<template>
  <div>
    <v-expansion-panels
      class="operationSimulationPanel elevation-6"
      :class="positionSimulatorClass"
      v-model="visiblePanels"
      flat
    >
      <v-expansion-panel flat>
        <v-expansion-panel-header
          expand-icon=""
          class="pb-3"
        >
          <div class="d-flex align-center simulator-header">
            <PhMoney
              size="24"
              class="flex-shrink-0"
            />
            <h3 class="expansion-title pl-2">
              {{ textOnSimulation }}
            </h3>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content
          eager
          :key="panelKey"
          class="py-2"
          flat
        >
          <div class="wrapper-position">
            <div v-if="leftSimulator">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    text
                    icon
                    class="btn-move"
                    @click="setPositionRight()"
                    v-on="on"
                  >
                    <PhArrowRight />
                  </v-btn>
                </template>
                <span>{{ $t('comum.operationSimulator.moveRight') }}</span>
              </v-tooltip>
            </div>
            <div v-else>
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    text
                    icon
                    class="btn-move"
                    @click="setPositionLeft()"
                    v-on="on"
                  >
                    <PhArrowLeft />
                  </v-btn>
                </template>
                <span>{{ $t('comum.operationSimulator.moveLeft') }}</span>
              </v-tooltip>
            </div>
          </div>
          <v-row
            class="py-2"
            no-gutters
          >
            <v-col
              cols="12"
              md="12"
            >
              <v-row
                class="pb-2"
                no-gutters
              >
                <v-col
                  cols="12"
                  md="12"
                >
                  <v-card class="box card-cambio pb-0">
                    <v-radio-group
                      v-model="calculationTypeId"
                      row
                      mandatory
                    >
                      <v-radio
                        v-for="(item, i) in calculationTypeList"
                        :key="i"
                        :value="item.id"
                        :label="item.description"
                      />
                    </v-radio-group>
                    <v-divider class="py-1" />
                    <v-radio-group
                      v-model="operation.exchangeBuy"
                      row
                      mandatory
                    >
                      <v-radio
                        :value="false"
                        :label="$t('comum.operationSimulator.sellExit')"
                      />
                      <v-radio
                        :value="true"
                        :label="$t('comum.operationSimulator.buyEntry')"
                      />
                    </v-radio-group>
                    <v-divider class="py-1" />
                    <v-radio-group
                      v-model="operation.exchangeType.id"
                      row
                      mandatory
                    >
                      <v-radio
                        v-for="(item, i) in filteredExchangeTypes"
                        :key="i"
                        :value="item.id"
                        :label="item.description"
                      />
                    </v-radio-group>
                  </v-card>
                </v-col>
                <v-col
                  cols="12"
                  md="12"
                >
                  <v-form
                    ref="formSimulatorOperation"
                    v-model="valid"
                    v-if="operation.exchangeType.id"
                  >
                    <div v-if="isExchangeType">
                      <SimulatorExchange
                        v-model="operation"
                        :calculation-type="calculationTypeId"
                      />
                    </div>
                    <div v-else>
                      <SimulatorRemittance
                        v-model="operation"
                        :calculation-type="calculationTypeId"
                        @resetAndUpdateSpread="resetAndUpdateSpread"
                      />
                    </div>
                  </v-form>
                </v-col>
                <v-col
                  cols="12"
                  md="12"
                >
                  <v-card class="card-cambio pa-5">
                    <v-btn
                      class="btn-secondary mr-2"
                      @click="copyCustomerMessageToClipboard"
                      text
                      :disabled="!operation.currency || !operation.currency.id"
                    >
                      <PhCopy aria-hidden="true" />
                      {{ $t('comum.operationSimulator.copyMessage') }}
                    </v-btn>
                    <v-btn
                      class="btn-tertiary"
                      @click="clearSimulation"
                      text
                    >
                      {{ $t('comum.operationSimulator.clear') }}
                    </v-btn>
                  </v-card>
                </v-col>
                <v-col
                  cols="12"
                  md="12"
                  class="text-right footer-actions"
                >
                  <v-btn
                    text
                    class="btn-tertiary"
                    @click="closeSimulation"
                  >
                    {{ $t('comum.operationSimulator.close') }}
                  </v-btn>
                  <v-btn
                    text
                    class="btn-primary"
                    @click="convertToOperation"
                  >
                    <PhCheck />
                    {{ $t('comum.operationSimulator.validate') }}
                  </v-btn>
                  <v-btn
                    text
                    class="btn-secondary"
                    @click="convertToOperationNewTab"
                  >
                    <PhLink /> {{ $t('comum.operationSimulator.validateNewTab') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
// Components
import SimulatorRemittance from '@/components/simulator-operation/SimulatorRemittance';
import SimulatorExchange from '@/components/simulator-operation/SimulatorExchange';

// APIs
import ExchangeBankTypeApi from '@/api/exchangeBank/exchange-bank-type-api';
import RateApi from '@/api/general/rate-api';

// Models
import OperationModel from '@/model/operation-model';
import ExchangeBankTypeModel from '@/model/exchange-bank-type-model';
import RateModel from '@/model/rate-model';

// Constants
import { OPERATION_CALCULATION_TYPE, OPERATION_TYPE } from '@/constants/general-constants';

// Utils
import operationUtils from '@/utils/operation';
import mixinMessage from '@/mixin/mixin-message';
import simulatorMessagesTemplate from '@/utils/simulatorMessagesTemplate';
import rateSelector from '@common/utils/rateSelector';
import ZapiApi from '@/api/external/zapi/zapi-api';
import { isCustomerWhatsAppEligible, buildCustomerWhatsAppPhone } from '@/utils/operationCustomerWhatsApp';

export default {
  name: 'OperationSimulator',
  components: {
    SimulatorRemittance,
    SimulatorExchange
  },
  mixins: [mixinMessage],
  inject: ['appData'],
  props: {
    isOpenSimulator: {
      type: Boolean,
      default: false
    }
  },
  model: {
    prop: 'isOpenSimulator',
    event: 'onChange'
  },
  data() {
    return {
      visiblePanels: 0,
      calculationTypeList: Object.values(OPERATION_CALCULATION_TYPE),
      calculationTypeId: OPERATION_CALCULATION_TYPE.CURRENCY_AMOUNT.id,
      exchangeTypes: [],
      operationTypeConstant: OPERATION_TYPE,
      operation: new OperationModel(),
      valid: false,
      timer: null,
      panelKey: 0,
      leftSimulator: false,
      usdValue: 0,
      apiExchangeBankType: new ExchangeBankTypeApi(this.appData.currentUser),
      rateApi: new RateApi(this.appData.currentUser)
    };
  },
  mounted() {
    this.getExchangeTypes();
  },
  watch: {
    'operation.exchangeBuy'() {
      this.resetSimulation();
    },
    'operation.exchangeType.id'() {
      this.resetSimulation();
    },
    'operation.nature'() {
      this.updateValues();
    },
    'operation.amount'() {
      if (this.calculationTypeId == OPERATION_CALCULATION_TYPE.CURRENCY_AMOUNT.id) this.updateValues();
    },
    'operation.currencyQuotation'() {
      this.updateValuesKeepingCustomerRate();
    },
    'operation.currency'() {
      operationUtils.resetSpreads(this.operation);
      this.getCurrencyQuotation(true);
    },
    'operation.bank'() {
      this.operation.exchangeContractCost = undefined;
      this.updateValues();
    },
    'operation.spread'() {
      this.updateValues();
    },
    'operation.customerRate'() {
      this.updateValuesKeepingCustomerRate();
    },
    'operation.exchangeContractCost'() {
      this.updateBrlExchangeContract();
    },
    'operation.deliveryCost'() {
      this.updateValues();
    },
    'operation.totalValue'() {
      if (this.calculationTypeId == OPERATION_CALCULATION_TYPE.BRL_AMOUNT.id) this.updateValues();
    },
    'operation.useUsdExchangeContract'() {
      this.updateBrlExchangeContract();
    },
    'operation.exchangeContractCostBrl'() {
      this.updateValues();
    },
    usdValue() {
      this.updateBrlExchangeContract();
    }
  },
  computed: {
    filteredExchangeTypes() {
      const filtered = this.exchangeTypes.filter((i) => i.id != this.operationTypeConstant.BOTH.id);
      filtered.sort((a, b) => a.id - b.id);

      return filtered;
    },

    isExchangeType() {
      return this.operation.exchangeType.id == this.operationTypeConstant.EXCHANGE.id;
    },

    textOnSimulation() {
      if (!this.operation.customer?.name) return this.$t('comum.operationSimulator.titleDefault');

      const operationType = this.isExchangeType
        ? this.$t('comum.operationSimulator.exchange')
        : this.$t('comum.operationSimulator.remittance');

      return this.$t('comum.operationSimulator.titleWithCustomer', {
        type: operationType,
        name: this.operation.customer.name
      });
    },

    positionSimulatorClass() {
      return this.leftSimulator ? 'leftSimulatorClass' : 'rightSimulatorClass';
    }
  },
  methods: {
    setPositionRight() {
      this.leftSimulator = false;
    },
    setPositionLeft() {
      this.leftSimulator = true;
    },

    async getExchangeTypes() {
      try {
        const response = await this.apiExchangeBankType.findAll();
        this.exchangeTypes = response.data.map((t) => new ExchangeBankTypeModel(t));
      } catch (error) {
        this.sendErrorMessage(error, this.$t('comum.operationSimulator.loadExchangeTypesError'));
      }
    },

    async getCurrencyQuotation(updateCustomerRate) {
      if (!this.operation.currency?.code) {
        clearTimeout(this.timer);
        this.operation.currencyQuotation = 0;

        return;
      }

      try {
        const response = await this.rateApi.findCommercial({ currencies: `${this.operation.currency.code}` });
        const rate = new RateModel(response.data[0]);

        this.operation.currencyQuotation = rateSelector.selectRate(rate, this.operation.exchangeBuy);

        this.getUsdQuotation();

        if (updateCustomerRate) this.updateValues();

        this.startCurrencyTimeout();
      } catch (error) {
        this.operation.currencyQuotation = 0;
        this.sendErrorMessage(error, this.$t('comum.operationSimulator.loadRateError'));
      }
    },

    startCurrencyTimeout() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.getCurrencyQuotation();
      }, operationUtils.REFRESH_RATE_TIME);
    },

    updateValues() {
      if (this.isExchangeType) this.updateValuesExchange();
      else this.updateValuesRemittance();
    },

    updateValuesRemittance() {
      operationUtils.setBaseValuesOperationRemittance(this.operation);
      this.calculateOperationRemittance();
    },

    updateValuesExchange() {
      operationUtils.setBaseValuesOperationExchange(this.operation);
      this.calculateOperationExchange();
    },

    calculateOperationExchange() {
      if (this.calculationTypeId == OPERATION_CALCULATION_TYPE.CURRENCY_AMOUNT.id) operationUtils.calculateOperationExchange(this.operation);
      else operationUtils.calculateOperationExchangeFromBrl(this.operation);
    },

    calculateOperationRemittance() {
      if (this.calculationTypeId == OPERATION_CALCULATION_TYPE.CURRENCY_AMOUNT.id) operationUtils.calculateOperationRemittance(this.operation);
      else operationUtils.calculateOperationRemittanceFromBrl(this.operation);
    },

    updateValuesKeepingCustomerRate() {
      if (this.isExchangeType) this.updateValuesKeepingCustomerRateExchange();
      else this.updateValuesKeepingCustomerRateRemittance();
    },

    updateValuesKeepingCustomerRateRemittance() {
      operationUtils.getSpreadFromCustomerRateRemittance(this.operation);
      this.calculateOperationRemittance();
    },

    updateValuesKeepingCustomerRateExchange() {
      operationUtils.getSpreadFromCustomerRateExchange(this.operation);
      this.calculateOperationExchange();
    },

    resetSimulation() {
      this.operation = new OperationModel({
        exchangeBuy: this.operation.exchangeBuy,
        exchangeType: this.operation.exchangeType
      });
      this.panelKey++;
    },

    resetAndUpdateSpread() {
      operationUtils.resetSpreads(this.operation);
      this.getCurrencyQuotation(true);
    },

    clearSimulation() {
      this.operation = new OperationModel();
      this.calculationTypeId = OPERATION_CALCULATION_TYPE.CURRENCY_AMOUNT.id;
      this.panelKey++;
    },

    closeSimulation() {
      this.visiblePanels = undefined;
      this.$emit('onChange', false);
    },

    async convertToOperation() {
      this.visiblePanels = undefined;

      try {
        await this.$router.push(this.mountOperationRouteObject());
        this.$emit('onChange', false);
        this.$router.go(0);
      } catch (error) { // eslint-disable-next-line no-empty
      }
    },

    convertToOperationNewTab() {
      this.$emit('onChange', false);
      const routeData = this.$router.resolve(this.mountOperationRouteObject());
      window.open(routeData.href, '_blank');
    },

    mountOperationRouteObject() {
      const op = this.operation;

      const add = (q, key, val) => {
        if (val !== undefined && val !== null && (typeof val !== 'string' || val !== '')) q[key] = val;
      };

      const query = {};
      add(query, 'idCustomer', op.customer?.id);
      add(query, 'idExchangeType', op.exchangeType?.id);
      add(query, 'idBank', op.bank?.id);
      add(query, 'idNature', op.nature?.id);
      add(query, 'idCurrency', op.currency?.id);
      add(query, 'amount', op.amount);
      add(query, 'rate', op.customerRate);
      add(query, 'contractCost', op.exchangeContractCost);
      add(query, 'city', op.storeCity);
      add(query, 'idStore', op.store?.id);
      add(query, 'idDeliveryType', op.deliveryType?.id);
      add(query, 'deliveryCost', op.deliveryCost);

      if (op.exchangeBuy !== undefined && op.exchangeBuy !== null) query.exchangeBuy = op.exchangeBuy;

      return { name: 'FormOperation', query };
    },

    async copyCustomerMessageToClipboard() {
      const text = simulatorMessagesTemplate.getCustomerMessage(this.operation);

      try {
        if (isCustomerWhatsAppEligible(this.operation)) {
          const zapi = new ZapiApi(this.appData.currentUser);
          const phone = buildCustomerWhatsAppPhone(this.operation);
          const res = await zapi.sendMessage(phone, text, this.operation.customer.id, this.operation.id);
          const failed = res.data?.error || res.data?.sent === false;

          if (!failed) {
            this.sendMessage(this.$t('comum.operationSimulator.messageSent'), 'success');

            return;
          }

          await navigator.clipboard.writeText(text);
          this.sendMessage(this.$t('comum.operationSimulator.messageSentFallback'), 'warning');

          return;
        }

        await navigator.clipboard.writeText(text);
        this.sendMessage(this.$t('comum.operationSimulator.messageCopied'), 'success');
      } catch {
        this.sendMessage(this.$t('comum.operationSimulator.messageCopyError'), 'error');
      }
    },

    async getUsdQuotation() {
      try {
        const response = await this.rateApi.findCommercial({ currencies: 'USD' });
        const rate = new RateModel(response.data[0]);
        this.usdValue = rateSelector.selectRate(rate, this.operation.exchangeBuy);
      } catch (error) {
        this.usdValue = 0;
        this.sendErrorMessage(error, this.$t('comum.operationSimulator.loadDollarError'));
      }
    },

    updateBrlExchangeContract() {
      if (!this.operation.useUsdExchangeContract) {
        this.operation.exchangeContractCostBrl = this.operation.exchangeContractCost;

        return;
      }

      this.operation.exchangeContractCostBrl = (
        +this.operation.exchangeContractCost * +this.usdValue
      ).toFixed(operationUtils.SHORT_DECIMALS_PLACES);
    }
  }
};
</script>

<style lang="scss" scoped>
.leftSimulatorClass {
  left: 100px;
}

.rightSimulatorClass {
  right: 100px;
}

.wrapper-position {
  display: flex;
  position: relative;
  justify-content: flex-end;
}

.btn-move {
  position: absolute;
  right: 20px;
  top: 20px;
  z-index: 9;
}

.operationSimulationPanel {
  position: fixed;
  max-width: 900px;
  border-radius: var(--tc-radius-sm);
  background-color: var(--v-backgroundSecondary-base);
  bottom: 20px;
  overflow: auto;
  width: auto;
  z-index: 9;
}

.v-expansion-panel-content {
  height: 70vh;
  overflow-y: scroll;
}

@media screen and (max-width: 942px) {
  .operationSimulationPanel {
    width: 90vw;
    margin: 0 auto;
    left: 0;
    right: 0;
  }

  .wrapper-position {
    display: none;
  }
}

.v-expansion-panel--active > .v-expansion-panel-header {
  min-height: 45px;
}

.expansion-title {
  font-size: 1rem;
  font-weight: 400;
  padding-right: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 24px;
  margin: 0;
}

.simulator-header {
  gap: 0;
  line-height: 1;
}

::v-deep .v-expansion-panels .v-expansion-panel-header .v-expansion-panel-header__icon i.v-icon {
  font-size: 1rem;
}

.footer-actions {
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: end;
}

@media screen and (max-width: 520px) {
  .footer-actions {
    flex-direction: column;
    gap: 10px;
  }

  .footer-actions > button {
    width: 100%;
  }
}
</style>
